import { CaseInsensitive, CheckoutProps, Currency, Locale, MintConfigs, PaymentMethod, SigninMethods, clientNames, getEnvironmentBaseUrl } from "@crossmint/client-sdk-base";
import { inject, injectable } from "@launchtray/tsyringe-async";
import { INetworkConfig } from "../../cityversetycoon/networking/config/INetworkConfig";
import { IGuestModel } from "../../cityversetycoon/scenes/common/models/IGuestModel";
import { VariableSaver } from "../../tools/variableSaver/VariableSaver";
import { AuthConstants } from "../../cityversetycoon/scenes/popups/login/AuthConstants";

type MintQueryParams = {
    clientId: string;
    projectId?: string;
    mintTo?: string;
    emailTo?: string;
    listingId?: string;
    clientName: string;
    clientVersion: string;
    mintConfig?: string;
    whPassThroughArgs?: string;
    paymentMethod?: PaymentMethod;
    preferredSigninMethod?: SigninMethods;
    prepay?: string;
    locale: Locale;
    currency: Currency;
    successCallbackURL?: string;
    failureCallbackURL?: string;
    checkoutProps?: string;
};

type PaymentTransitionParams = {
    prepareMintUrl: string;
    urlOrigin: string;
    requestInput: {
        method: string,
        headers: Record<string, string>,
        body: any
    },
    checkoutUrlObject: MintQueryParams
}

@injectable()
export class PaymentUrlResolver {
    private readonly networkConfig: INetworkConfig;
    private readonly guestModel: IGuestModel;
    private readonly queryBaseURL: string;
    private readonly variableSaver: VariableSaver;

    public constructor(
        @inject("INetworkConfig") networkConfig: INetworkConfig,
        @inject("IGuestModel") guestModel: IGuestModel,
        variableSaver: VariableSaver
    ) {
        this.networkConfig = networkConfig;
        this.guestModel = guestModel;
        this.variableSaver = variableSaver;
        this.queryBaseURL = `${this.networkConfig.apiURL}api/${this.networkConfig.apiVersion}`;
    }

    public resolveCheckoutUrl(
        clientId: string,
        projectId: string,
        clientName: clientNames,
        environment: string,
        libVersion: string,
        currency: CaseInsensitive<Currency>,
        locale: Locale,
        mintConfig?: MintConfigs,
        successCallbackURL?: string,
        failureCallbackURL?: string,
        mintTo?: string,
        emailTo?: string,
        listingId?: string,
        whPassThroughArgs?: any,
        paymentMethod?: PaymentMethod,
        preferredSigninMethod?: SigninMethods,
        prepay?: boolean,
        checkoutProps?: CheckoutProps
    ): string {
        const urlOrigin = getEnvironmentBaseUrl(environment);
        const mintQueryParams = this.prepareObjectForCheckout(
            clientId,
            projectId,
            clientName,
            libVersion,
            currency,
            locale,
            mintConfig,
            successCallbackURL,
            failureCallbackURL,
            mintTo,
            emailTo,
            listingId,
            whPassThroughArgs,
            paymentMethod,
            preferredSigninMethod,
            prepay,
            checkoutProps
        );

        return `${urlOrigin}/checkout?${new URLSearchParams(mintQueryParams).toString()}`;
    }

    public resolveCheckoutUrlWithMintBackendRequest(
        buildingId: string,
        clientId: string,
        projectId: string,
        clientName: clientNames,
        environment: string,
        libVersion: string,
        currency: CaseInsensitive<Currency>,
        locale: Locale,
        successCallbackURL?: string,
        failureCallbackURL?: string,
        mintTo?: string,
        emailTo?: string,
        listingId?: string,
        whPassThroughArgs?: any,
        paymentMethod?: PaymentMethod,
        preferredSigninMethod?: SigninMethods,
        prepay?: boolean,
        checkoutProps?: CheckoutProps,
        couponBenefitIds?: Array<string>
    ): string {
        const urlOrigin = getEnvironmentBaseUrl(environment);
        const mintQueryParams = this.prepareObjectForCheckout(
            clientId,
            projectId,
            clientName,
            libVersion,
            currency,
            locale,
            {},
            successCallbackURL,
            failureCallbackURL,
            mintTo,
            emailTo,
            listingId,
            whPassThroughArgs,
            paymentMethod,
            preferredSigninMethod,
            prepay,
            checkoutProps
        );
        const fetchBody = { couponBenefitIds };
        const paymentTransitionParams: PaymentTransitionParams = {
            prepareMintUrl: `${this.queryBaseURL}${this.networkConfig.marketplaceBuildingsPath}/${buildingId}/${this.networkConfig.prepareMintPath}`,
            requestInput: {
                method: "POST",
                headers: {
                    "X-Guest-Id": this.guestModel.id.value,
                    "Authorization": `Bearer ${this.variableSaver.getString(AuthConstants.ACCESS_TOKEN)}`,
                    "Content-Type": "application/json"
                },
                body: fetchBody
            },
            checkoutUrlObject: mintQueryParams,
            urlOrigin
        };
        const data = JSON.stringify({
            prepareMintUrl: paymentTransitionParams.prepareMintUrl,
            requestInput: paymentTransitionParams.requestInput,
            checkoutUrlObject: paymentTransitionParams.checkoutUrlObject,
            urlOrigin: paymentTransitionParams.urlOrigin
        });
        return `${window.location.origin}/payment-transition/?data=${new URLSearchParams(data)}`;
    }

    private prepareObjectForCheckout(
        clientId: string,
        projectId: string,
        clientName: clientNames,
        libVersion: string,
        currency: CaseInsensitive<Currency>,
        locale: Locale,
        mintConfig?: MintConfigs,
        successCallbackURL?: string,
        failureCallbackURL?: string,
        mintTo?: string,
        emailTo?: string,
        listingId?: string,
        whPassThroughArgs?: any,
        paymentMethod?: PaymentMethod,
        preferredSigninMethod?: SigninMethods,
        prepay?: boolean,
        checkoutProps?: CheckoutProps
    ): MintQueryParams {
        const mintQueryParams: MintQueryParams = {
            clientId,
            clientName,
            clientVersion: libVersion,
            locale,
            currency: currency.toLowerCase() as Currency,
        };

        if (mintConfig) mintQueryParams.mintConfig = JSON.stringify(mintConfig);
        if (successCallbackURL) mintQueryParams.successCallbackURL = successCallbackURL;
        if (failureCallbackURL) mintQueryParams.failureCallbackURL = failureCallbackURL;
        if (mintTo) mintQueryParams.mintTo = mintTo;
        if (emailTo) mintQueryParams.emailTo = emailTo;
        if (listingId) mintQueryParams.listingId = listingId;
        if (whPassThroughArgs) mintQueryParams.whPassThroughArgs = JSON.stringify(whPassThroughArgs);
        if (paymentMethod) mintQueryParams.paymentMethod = paymentMethod.toLowerCase() as PaymentMethod;
        if (preferredSigninMethod) mintQueryParams.preferredSigninMethod = preferredSigninMethod;
        if (prepay) mintQueryParams.prepay = "true";
        if (projectId) mintQueryParams.projectId = projectId;
        if (checkoutProps && checkoutProps.experimental === true)
            mintQueryParams.checkoutProps = JSON.stringify(checkoutProps);

        return mintQueryParams;
    }
}